"use client";

import { Capacitor } from "@capacitor/core";
import { ChevronLeft } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { installSwipeBack } from "@/lib/swipe-back/install-swipe-back";

/**
 * Wraps every route so the edge-swipe-back gesture has a page to move.
 *
 * At rest the two wrappers are plain, unstyled block boxes: no class, no
 * inline style, no stacking context, so nothing about layout, `fixed` or
 * `sticky` positioning changes. They only take on styles for the length of a
 * swipe — see `installSwipeBack` for why the page has to be "frozen" first.
 *
 * Native app only. Mobile Safari and Android Chrome own the left edge for
 * their own history gestures, and a second, competing swipe there would
 * navigate twice.
 */
export function SwipeBackStage({ children }: { children: ReactNode }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const stage = stageRef.current;
    const page = pageRef.current;
    const backdrop = backdropRef.current;
    const scrim = scrimRef.current;
    const indicator = indicatorRef.current;
    const preview = previewRef.current;
    if (!stage || !page || !backdrop || !scrim || !indicator || !preview)
      return;
    return installSwipeBack({
      stage,
      page,
      backdrop,
      scrim,
      indicator,
      preview,
    });
  }, []);

  return (
    <>
      {/* A captured destination viewport, or the app surface on a cold link.
          Neither layer is interactive or part of React's hidden route tree. */}
      <div
        ref={backdropRef}
        hidden
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 overflow-hidden bg-surface"
      >
        <div
          ref={previewRef}
          className="absolute inset-0 [&>img]:h-full [&>img]:w-full"
        />
        <div ref={scrimRef} className="absolute inset-0 bg-black opacity-0" />
      </div>
      <div ref={stageRef} data-swipe-back-stage="">
        <div ref={pageRef} data-swipe-back-page="">
          {children}
        </div>
      </div>
      {/* Reduced motion: the page stays put and this still marker stands in
          for it, filling once a release would go back. */}
      <div
        ref={indicatorRef}
        hidden
        aria-hidden="true"
        data-armed="false"
        className="pointer-events-none fixed left-2 z-10100 flex size-11 items-center justify-center rounded-full border border-white/30 bg-background/40 text-text-primary opacity-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_6px_20px_-6px_rgba(0,0,0,0.45)] backdrop-blur-xl backdrop-saturate-150 data-[armed=true]:bg-brand/80 data-[armed=true]:text-white"
      >
        <ChevronLeft className="size-5" strokeWidth={2.5} />
      </div>
    </>
  );
}
